"use client";

import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import type React from "react";
import { cn } from "@/lib/utils";

/**
 * Avatar — a circular identity chip that shows an image when available and
 * falls back to the initials derived from `name`. Ported from the Calm
 * Authority design system (docs/design-system/components/core/Avatar), used by
 * the shell's WorkspaceSwitcher and AccountChip.
 *
 * `size` is a pixel diameter (the design system sizes avatars numerically), and
 * `tone` selects a token-driven fill. Unknown tones fall back to neutral.
 */

export type AvatarTone = "brand" | "neutral" | "info";

const TONE_CLASS: Record<AvatarTone, string> = {
  brand: "bg-[var(--brand-soft)] text-[var(--brand-ink)]",
  info: "bg-[var(--info-soft)] text-[var(--info-ink)]",
  neutral: "bg-[var(--surface-3)] text-[var(--ink-2)]",
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface AvatarProps
  extends Omit<AvatarPrimitive.Root.Props, "children"> {
  name: string;
  src?: string;
  size?: number;
  // Known tones autocomplete; arbitrary tone strings (from design-system data)
  // are accepted and fall back to neutral. `string & {}` keeps the union from
  // collapsing to `string`.
  tone?: AvatarTone | (string & {});
}

export function Avatar({
  name,
  src,
  size = 32,
  tone = "neutral",
  className,
  style,
  ...props
}: AvatarProps): React.ReactElement {
  const toneClass =
    TONE_CLASS[(tone as AvatarTone) in TONE_CLASS ? (tone as AvatarTone) : "neutral"];

  return (
    <AvatarPrimitive.Root
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold",
        toneClass,
        className,
      )}
      data-slot="avatar"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        ...style,
      }}
      {...props}
    >
      {src ? (
        <AvatarPrimitive.Image
          className="size-full object-cover"
          data-slot="avatar-image"
          src={src}
        />
      ) : null}
      <AvatarPrimitive.Fallback
        className="flex size-full items-center justify-center"
        data-slot="avatar-fallback"
      >
        {initialsFromName(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export { AvatarPrimitive, initialsFromName };
